import { useEffect, useState } from "react";
import { PtBadge } from "@/components/PtBadge";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const PALETTE = ["#EAB308", "#16A34A", "#2563EB", "#DC2626", "#9333EA", "#EA580C"];

const parse = (v) => {
  const m = String(v).trim().match(/^(\d+)\.(\d+)$/);
  return m ? [Number(m[1]), Number(m[2])] : null;
};

export function CompanyRangeDialog({ device, onClose, onSaved }) {
  const [from, setFrom] = useState("1.1");
  const [to, setTo] = useState(`1.${device.max_slots}`);
  const [company, setCompany] = useState("");
  const [busy, setBusy] = useState(false);
  const [pts, setPts] = useState([]);
  const loadPts = () => api.get("/companies").then((r) => setPts(r.data));
  useEffect(() => { loadPts(); }, []);
  const setColor = async (name, color) => {
    try {
      await api.put("/companies/color", { name, color });
      setPts((l) => l.map((p) => (p.name === name ? { ...p, color } : p)));
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const save = async (clear) => {
    const a = parse(from), b = parse(to);
    if (!a || !b) return toast.error("Format slot harus port.slot, mis. 2.1");
    if (!clear && !company.trim()) return toast.error("Isi nama PT");
    setBusy(true);
    try {
      const r = await api.post("/slots/company-range", { device_id: device.id, from_port: a[0], from_slot: a[1], to_port: b[0], to_slot: b[1], company: clear ? "" : company });
      toast.success(`${r.data.updated} slot (${r.data.range}) → ${r.data.company || "tanpa PT"}`);
      onSaved();
      loadPts();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="company-range-dialog" className="bg-white border-slate-300 max-w-md">
        <DialogHeader><DialogTitle className="font-display text-2xl">Tandai PT · {device.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Semua slot dari awal sampai akhir rentang (berurutan per port) akan ditandai milik PT ini. Contoh: 2.1 sampai 10.6.</p>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs text-slate-500 space-y-1">Dari slot<Input data-testid="company-range-from" value={from} onChange={(e) => setFrom(e.target.value)} className="bg-white font-mono" /></label>
          <label className="text-xs text-slate-500 space-y-1">Sampai slot<Input data-testid="company-range-to" value={to} onChange={(e) => setTo(e.target.value)} className="bg-white font-mono" /></label>
        </div>
        <label className="text-xs text-slate-500 space-y-1">Nama PT<Input data-testid="company-range-name" placeholder="mis. BP" value={company} onChange={(e) => setCompany(e.target.value)} className="bg-white" /></label>
        <div className="flex gap-2">
          <Button data-testid="company-range-save" disabled={busy} onClick={() => save(false)} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white">Simpan</Button>
          <Button data-testid="company-range-clear" disabled={busy} variant="outline" onClick={() => save(true)}>Hapus PT di rentang ini</Button>
        </div>
        {pts.length > 0 && (
          <div className="border-t border-slate-200 pt-3 space-y-2" data-testid="pt-colors">
            <p className="text-xs uppercase tracking-wider text-slate-500">Warna label PT</p>
            {pts.map((p) => (
              <div key={p.name} className="flex items-center gap-2 text-sm">
                <PtBadge name={p.name} colors={{ [p.name]: p.color }} className="text-[11px] px-1.5" />
                <span className="text-xs text-slate-500">{p.slots} slot</span>
                <div className="ml-auto flex items-center gap-1">
                  {PALETTE.map((c) => (
                    <button key={c} data-testid={`pt-color-${p.name}-${c.slice(1)}`} onClick={() => setColor(p.name, c)} title={c}
                      className={`h-5 w-5 rounded-full border-2 ${p.color?.toLowerCase() === c.toLowerCase() ? "border-slate-900" : "border-white"}`} style={{ backgroundColor: c }} />
                  ))}
                  <input type="color" data-testid={`pt-color-custom-${p.name}`} value={p.color} onChange={(e) => setColor(p.name, e.target.value)} className="h-6 w-6 cursor-pointer bg-transparent" title="Warna lain" />
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
